<template>
    <div class="flex min-h-[70vh] bg-white rounded-lg shadow-sm overflow-hidden">
        <!-- Sidebar Navigation -->
        <nav class="w-56 flex-shrink-0 border-r border-gray-200 bg-gray-50">
            <div class="px-4 py-5 border-b border-gray-200">
                <h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wider">Settings</h2>
            </div>
            <ul class="py-2">
                <li v-for="section in sections" :key="section.id">
                    <button
                        @click="activeSection = section.id"
                        :class="[
                            'w-full flex items-center px-4 py-3 text-sm transition-colors duration-150',
                            activeSection === section.id
                                ? 'bg-logo-light text-logo-dark font-medium border-r-2 border-logo'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        ]"
                    >
                        <span v-html="section.icon" class="w-5 h-5 mr-3 flex-shrink-0"></span>
                        <span>{{ section.label }}</span>
                        <span
                            v-if="section.id === 'notifications' && hasUnsetPush"
                            class="ml-auto w-2 h-2 rounded-full bg-gold"
                        ></span>
                    </button>
                </li>
            </ul>
        </nav>

        <!-- Content Area -->
        <div class="flex-1 min-w-0">
            <!-- Section Header -->
            <div class="px-8 py-5 border-b border-gray-200 flex items-center justify-between">
                <div>
                    <h1 class="text-xl font-semibold text-gray-900">{{ currentSection.label }}</h1>
                    <p class="mt-1 text-sm text-gray-500">{{ currentSection.description }}</p>
                </div>
                <transition name="fade">
                    <span v-if="saving" class="inline-flex items-center text-sm text-gray-400">
                        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-logo" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                        Saving...
                    </span>
                </transition>
            </div>

            <!-- Profile Section -->
            <div v-show="activeSection === 'profile'" class="px-8 py-6">
                <div class="max-w-lg space-y-6">
                    <div>
                        <label for="phone" class="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                        <input
                            type="tel"
                            id="phone"
                            v-model="form.phone"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-logo focus:border-transparent transition-shadow"
                            placeholder="555-123-4567"
                        >
                        <p v-if="errors.phone" class="mt-1 text-sm text-rose">{{ errors.phone[0] }}</p>
                    </div>

                    <div>
                        <label for="experience" class="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
                        <input
                            type="text"
                            id="experience"
                            v-model="form.experience"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-logo focus:border-transparent transition-shadow"
                            placeholder="e.g., 5 years"
                        >
                        <p v-if="errors.experience" class="mt-1 text-sm text-rose">{{ errors.experience[0] }}</p>
                    </div>

                    <div>
                        <label for="credential_name" class="block text-sm font-medium text-gray-700 mb-1">Credential</label>
                        <input
                            type="text"
                            id="credential_name"
                            v-model="form.credential_name"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-logo focus:border-transparent transition-shadow"
                            placeholder="e.g., CCS, CPC"
                        >
                        <p v-if="errors.credential_name" class="mt-1 text-sm text-rose">{{ errors.credential_name[0] }}</p>
                    </div>
                </div>
            </div>

            <!-- EMR Systems Section -->
            <div v-show="activeSection === 'emr'" class="px-8 py-6">
                <p class="text-sm text-gray-500 mb-5">Select the EMR systems you are trained to work with. You'll only receive assignments from agencies using these systems.</p>
                <div class="grid grid-cols-2 gap-x-6 gap-y-3 max-w-lg">
                    <label
                        v-for="emr in initialData.all_emrs"
                        :key="emr.id"
                        class="relative flex items-center py-2 px-3 rounded-md cursor-pointer transition-colors duration-150"
                        :class="form.emr_ids.includes(emr.id) ? 'bg-logo-light bg-opacity-40' : 'hover:bg-gray-50'"
                    >
                        <input
                            type="checkbox"
                            :value="emr.id"
                            v-model="form.emr_ids"
                            class="h-4 w-4 text-logo border-gray-300 rounded focus:ring-logo"
                        >
                        <span class="ml-3 text-sm" :class="form.emr_ids.includes(emr.id) ? 'text-logo-dark font-medium' : 'text-gray-700'">{{ emr.name }}</span>
                    </label>
                </div>
            </div>

            <!-- Documents Section -->
            <div v-show="activeSection === 'documents'" class="px-8 py-6">
                <div class="max-w-lg space-y-6">
                    <div class="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div class="flex items-center">
                            <div class="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" :class="initialData.resume_at ? 'bg-mint-light' : 'bg-gray-100'">
                                <svg class="w-5 h-5" :class="initialData.resume_at ? 'text-mint-dark' : 'text-gray-400'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-900">Resume</p>
                                <p class="text-xs" :class="initialData.resume_at ? 'text-mint-dark' : 'text-gray-500'">
                                    {{ initialData.resume_at ? 'Uploaded' : 'Not uploaded' }}
                                </p>
                            </div>
                        </div>
                        <label class="cursor-pointer inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                            {{ initialData.resume_at ? 'Replace' : 'Upload' }}
                            <input type="file" class="hidden" @change="uploadResume" accept=".pdf,.doc,.docx">
                        </label>
                    </div>

                    <div class="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div class="flex items-center">
                            <div class="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" :class="initialData.w9_at ? 'bg-mint-light' : 'bg-gray-100'">
                                <svg class="w-5 h-5" :class="initialData.w9_at ? 'text-mint-dark' : 'text-gray-400'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-900">W9</p>
                                <p class="text-xs" :class="initialData.w9_at ? 'text-mint-dark' : 'text-gray-500'">
                                    {{ initialData.w9_at ? 'Uploaded' : 'Not uploaded' }}
                                </p>
                            </div>
                        </div>
                        <label class="cursor-pointer inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                            {{ initialData.w9_at ? 'Replace' : 'Upload' }}
                            <input type="file" class="hidden" @change="uploadW9" accept=".pdf,.doc,.docx">
                        </label>
                    </div>
                </div>
            </div>

            <!-- Notifications Section -->
            <div v-show="activeSection === 'notifications'" class="px-8 py-6">
                <!-- Push Permission Banner -->
                <div v-if="pushPermission === 'default'" class="mb-6 p-4 bg-logo-light bg-opacity-30 border border-logo-light rounded-lg flex items-center justify-between">
                    <div class="flex items-center">
                        <svg class="w-5 h-5 text-logo mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        <div>
                            <p class="text-sm font-medium text-logo-dark">Enable Push Notifications</p>
                            <p class="text-xs text-gray-600 mt-0.5">Get real-time desktop alerts when assignments are available.</p>
                        </div>
                    </div>
                    <button @click="enablePush" class="ml-4 px-4 py-2 bg-logo text-white text-sm font-medium rounded-md hover:bg-logo-dark transition-colors">
                        Enable
                    </button>
                </div>
                <div v-else-if="pushPermission === 'denied'" class="mb-6 p-4 bg-gold-light bg-opacity-30 border border-gold-light rounded-lg">
                    <div class="flex items-center">
                        <svg class="w-5 h-5 text-gold-dark mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <div>
                            <p class="text-sm font-medium text-gold-dark">Push Notifications Blocked</p>
                            <p class="text-xs text-gray-600 mt-0.5">Enable them in your browser settings to receive desktop alerts.</p>
                        </div>
                    </div>
                </div>

                <div class="max-w-lg">
                    <div class="space-y-1">
                        <!-- Table Header -->
                        <div class="flex items-center py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <span class="flex-1">Event</span>
                            <span class="w-16 text-center">Email</span>
                            <span class="w-16 text-center">Push</span>
                        </div>

                        <!-- Notification Rows -->
                        <div
                            v-for="event in notificationEvents"
                            :key="event.key"
                            class="flex items-center py-3 px-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            <div class="flex-1 min-w-0">
                                <p class="text-sm font-medium text-gray-900">{{ event.label }}</p>
                                <p class="text-xs text-gray-500 mt-0.5">{{ event.description }}</p>
                            </div>
                            <div class="w-16 flex justify-center">
                                <button
                                    type="button"
                                    @click="form.notification_prefs[event.key].email = !form.notification_prefs[event.key].email"
                                    :class="[
                                        'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-logo focus:ring-offset-2',
                                        form.notification_prefs[event.key].email ? 'bg-logo' : 'bg-gray-200'
                                    ]"
                                    role="switch"
                                    :aria-checked="form.notification_prefs[event.key].email"
                                    :data-notification-toggle="event.key + '-email'"
                                >
                                    <span
                                        :class="[
                                            'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                                            form.notification_prefs[event.key].email ? 'translate-x-4' : 'translate-x-0'
                                        ]"
                                    ></span>
                                </button>
                            </div>
                            <div class="w-16 flex justify-center">
                                <button
                                    type="button"
                                    @click="togglePush(event.key)"
                                    :disabled="pushPermission !== 'granted'"
                                    :class="[
                                        'relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-logo focus:ring-offset-2',
                                        pushPermission !== 'granted' ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                                        form.notification_prefs[event.key].push ? 'bg-logo' : 'bg-gray-200'
                                    ]"
                                    role="switch"
                                    :aria-checked="form.notification_prefs[event.key].push"
                                    :data-notification-toggle="event.key + '-push'"
                                >
                                    <span
                                        :class="[
                                            'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                                            form.notification_prefs[event.key].push ? 'translate-x-4' : 'translate-x-0'
                                        ]"
                                    ></span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="px-8 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                <a
                    :href="initialData.dashboard_url"
                    class="inline-flex items-center px-5 py-2 bg-logo text-white text-sm font-medium rounded-md hover:bg-logo-dark focus:outline-none focus:ring-2 focus:ring-logo focus:ring-offset-2 transition-colors"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Dashboard
                </a>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        initialData: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            activeSection: 'profile',
            form: {
                phone: this.initialData.phone || '',
                experience: this.initialData.experience || '',
                credential_name: this.initialData.credential_name || '',
                notification_prefs: this.initialData.notification_prefs || {
                    new_assignments: { email: false, push: true },
                    urgent_assignment: { email: true, push: true },
                    problem_resolved: { email: true, push: true },
                    assignment_reopened: { email: true, push: true },
                },
                emr_ids: this.initialData.emr_ids || [],
            },
            errors: {},
            saving: false,
            saveTimeout: null,
            initialized: false,
            pushPermission: 'default',
            sections: [
                {
                    id: 'profile',
                    label: 'Profile',
                    description: 'Your contact information and credentials.',
                    icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>',
                },
                {
                    id: 'emr',
                    label: 'EMR Systems',
                    description: 'Manage your EMR system training.',
                    icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>',
                },
                {
                    id: 'documents',
                    label: 'Documents',
                    description: 'Upload and manage required documents.',
                    icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>',
                },
                {
                    id: 'notifications',
                    label: 'Notifications',
                    description: 'Choose how you want to be notified.',
                    icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>',
                },
            ],
            notificationEvents: [
                { key: 'new_assignments', label: 'New Assignments', description: 'When new orders are submitted for your EMR systems' },
                { key: 'urgent_assignment', label: 'Urgent Assignments', description: 'When an urgent assignment becomes available' },
                { key: 'problem_resolved', label: 'Problem Resolved', description: 'When an agency resolves a problem you reported' },
                { key: 'assignment_reopened', label: 'Assignment Reopened', description: 'When a completed assignment is reopened' },
            ],
        };
    },

    computed: {
        currentSection() {
            return this.sections.find(s => s.id === this.activeSection) || this.sections[0];
        },
        hasUnsetPush() {
            return this.pushPermission === 'default';
        },
    },

    watch: {
        form: {
            deep: true,
            handler() {
                if (!this.initialized) {
                    return;
                }
                this.debouncedSave();
            }
        }
    },

    mounted() {
        if ('Notification' in window) {
            this.pushPermission = Notification.permission;
        }
        if (this.pushPermission === 'granted' && window.PushNotifications) {
            window.PushNotifications.ensureSubscription();
        }

        this.$nextTick(() => {
            this.initialized = true;
        });
    },

    methods: {
        togglePush(eventKey) {
            if (this.pushPermission !== 'granted') return;
            this.form.notification_prefs[eventKey].push = !this.form.notification_prefs[eventKey].push;
        },

        debouncedSave() {
            if (this.saveTimeout) {
                clearTimeout(this.saveTimeout);
            }
            this.saveTimeout = setTimeout(() => {
                this.save();
            }, 1000);
        },

        async save() {
            this.saving = true;
            this.errors = {};

            try {
                await axios.patch('/coder/settings', this.form);
                this.$toast.success('Settings saved');
            } catch (error) {
                if (error.response?.status === 422) {
                    this.errors = error.response.data.errors || {};
                    this.$toast.error('Please fix the errors below');
                } else {
                    this.$toast.error('Failed to save settings');
                }
            } finally {
                this.saving = false;
            }
        },

        async enablePush() {
            if (!window.PushNotifications) {
                this.$toast.error('Push notifications are not available');
                return;
            }
            try {
                const result = await window.PushNotifications.subscribe();
                this.pushPermission = Notification.permission;
                if (result) {
                    this.$toast.success('Desktop notifications enabled');
                } else if (Notification.permission === 'denied') {
                    this.$toast.error('Notifications were blocked by your browser');
                }
            } catch (e) {
                console.error('Push subscription failed:', e);
                this.$toast.error('Failed to enable notifications. Please try again later.');
            }
        },

        async uploadResume(event) {
            const file = event.target.files[0];
            if (!file) return;

            const formData = new FormData();
            formData.append('attachment', file);

            try {
                await axios.post('/coders/upload_resume', formData);
                this.$toast.success('Resume uploaded successfully');
                this.initialData.resume_at = new Date().toISOString();
            } catch (error) {
                this.$toast.error('Failed to upload resume');
            }
        },

        async uploadW9(event) {
            const file = event.target.files[0];
            if (!file) return;

            const formData = new FormData();
            formData.append('attachment', file);

            try {
                await axios.post('/coders/upload_w9', formData);
                this.$toast.success('W9 uploaded successfully');
                this.initialData.w9_at = new Date().toISOString();
            } catch (error) {
                this.$toast.error('Failed to upload W9');
            }
        },
    },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>
